import type { GridColumn, GridSelectOption } from '@/components/blocks/data-grid'

import { AvatarCell } from '@/components/shared'
import { count } from '@/lib'
import type { Arena, ConfigEntry, GameCard, Rarity } from '@/models'

const RARITY_CHIP: Record<Rarity, string> = {
  Common: 'bg-bg-subtle text-fg-muted',
  Rare: 'bg-accent/12 text-accent',
  Epic: 'bg-warn-surface text-warn',
  Legendary: 'bg-ok-surface text-ok',
}

const RARITY_ORDER: Rarity[] = ['Common', 'Rare', 'Epic', 'Legendary']

const RARITY_OPTIONS: GridSelectOption[] = RARITY_ORDER.map((rarity) => ({
  value: rarity,
  label: rarity,
  chipClassName: RARITY_CHIP[rarity],
}))

export const CARD_COLUMNS: GridColumn<GameCard>[] = [
  {
    key: 'id',
    header: 'ID',
    width: '110px',
    type: 'read',
    sortable: true,
    render: (row) => row.id,
  },
  {
    key: 'name',
    header: 'Card',
    width: 'minmax(180px,1.5fr)',
    type: 'text',
    sortable: true,
    prefix: (row) => <AvatarCell name={row.name} shape="square" className="ml-1" />,
    inputClassName: 'font-medium',
  },
  {
    key: 'rarity',
    header: 'Rarity',
    width: '120px',
    type: 'select',
    sortable: true,
    options: RARITY_OPTIONS,
    // Sort by power, not alphabetically: that is what someone balancing cards
    // means when they sort on rarity.
    value: (row) => RARITY_ORDER.indexOf(row.rarity),
  },
  {
    key: 'elixir',
    header: 'Elixir',
    width: '92px',
    type: 'number',
    align: 'end',
    sortable: true,
    inputClassName: 'font-semibold text-elixir',
  },
  { key: 'damage', header: 'Damage', width: '108px', type: 'number', align: 'end', sortable: true },
  { key: 'hp', header: 'HP', width: '88px', type: 'number', align: 'end', sortable: true },
  {
    key: 'usage',
    header: 'Usage',
    width: '96px',
    type: 'read',
    align: 'end',
    sortable: true,
    render: (row) => `${row.usage}%`,
  },
  {
    key: 'winRate',
    header: 'Win rate',
    width: '104px',
    type: 'read',
    align: 'end',
    sortable: true,
    render: (row) => `${row.winRate.toFixed(1)}%`,
  },
]

export const ARENA_COLUMNS: GridColumn<Arena>[] = [
  {
    key: 'id',
    header: 'ID',
    width: '90px',
    type: 'read',
    sortable: true,
    render: (row) => row.id,
  },
  {
    key: 'name',
    header: 'Arena',
    width: 'minmax(180px,1.5fr)',
    type: 'text',
    sortable: true,
    prefix: (row) => <AvatarCell name={row.name} shape="square" className="ml-1" />,
    inputClassName: 'font-medium',
  },
  {
    key: 'trophyMin',
    header: 'Trophy min',
    width: '124px',
    type: 'number',
    align: 'end',
    sortable: true,
  },
  {
    key: 'trophyMax',
    header: 'Trophy max',
    width: '124px',
    type: 'number',
    align: 'end',
    sortable: true,
  },
  { key: 'unlocks', header: 'Unlocks', width: 'minmax(150px,1fr)', type: 'text', sortable: true },
  {
    key: 'players',
    header: 'Players',
    width: '110px',
    type: 'read',
    align: 'end',
    sortable: true,
    render: (row) => count(row.players),
  },
]

/** Economy and Matchmaking are the same shape, so they share one column set. */
export const ENTRY_COLUMNS: GridColumn<ConfigEntry>[] = [
  {
    key: 'key',
    header: 'Key',
    width: 'minmax(200px,1.2fr)',
    type: 'text',
    sortable: true,
    inputClassName: 'text-sm',
  },
  {
    key: 'label',
    header: 'Setting',
    width: 'minmax(180px,1.5fr)',
    type: 'text',
    sortable: true,
    inputClassName: 'font-medium',
  },
  { key: 'value', header: 'Value', width: '130px', type: 'text', align: 'end', sortable: true },
  { key: 'unit', header: 'Unit', width: '110px', type: 'text' },
]
